import React, { useState } from "react";
import { m, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import OptimizedImage from "@/components/OptimizedImage";
import { useLanguage } from "@/hooks/useLanguage";

import gallery1 from "@/assets/gallery/gallery1.webp";
import gallery2 from "@/assets/gallery/gallery2.webp";
import gallery3 from "@/assets/gallery/gallery3.webp";
import gallery4 from "@/assets/gallery/gallery4.webp";
import gallery5 from "@/assets/gallery/gallery5.webp";
import gallery6 from "@/assets/gallery/gallery6.webp";
import gallery7 from "@/assets/gallery/gallery7.webp";
import gallery8 from "@/assets/gallery/gallery8.webp";
import gallery9 from "@/assets/gallery/gallery9.webp";
import gallery10 from "@/assets/gallery/gallery10.webp";
import gallery11 from "@/assets/gallery/gallery11.webp";
import gallery12 from "@/assets/gallery/gallery12.webp";
import gallery13 from "@/assets/gallery/gallery13.webp";
import gallery14 from "@/assets/gallery/gallery14.webp";
import gallery15 from "@/assets/gallery/gallery15.webp";
import gallery16 from "@/assets/gallery/gallery16.webp";

const imageKeys = [
  { src: gallery1, altKey: "gallery.alt.paperBaler1" },
  { src: gallery2, altKey: "gallery.alt.paperBaler2" },
  { src: gallery3, altKey: "gallery.alt.paperCardboardPlastic" },
  { src: gallery4, altKey: "gallery.alt.paperBaler3" },
  { src: gallery5, altKey: "gallery.alt.paperBaler4" },
  { src: gallery6, altKey: "gallery.alt.paperBaler5" },
  { src: gallery7, altKey: "gallery.alt.textileBaler" },
  { src: gallery8, altKey: "gallery.alt.recyclingYard1" },
  { src: gallery9, altKey: "gallery.alt.recyclingYard2" },
  { src: gallery10, altKey: "gallery.alt.recyclingYard3" },
  { src: gallery11, altKey: "gallery.alt.baler800Max" },
  { src: gallery12, altKey: "gallery.alt.balerGeneric1" },
  { src: gallery13, altKey: "gallery.alt.baler50Plus" },
  { src: gallery14, altKey: "gallery.alt.balerGeneric2" },
  { src: gallery15, altKey: "gallery.alt.polystyrene1" },
  { src: gallery16, altKey: "gallery.alt.polystyrene2" },
];

// Gallery grid image sizes - 4 columns on xl, 3 on lg, 2 on sm, 1 on mobile
const GALLERY_SIZES = "(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw";

const Gallery = () => {
  const { t } = useTranslation();
  const { localePath } = useLanguage();
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  const images = imageKeys.map((img) => ({ src: img.src, alt: t(img.altKey) }));

  const goNext = () => setSelectedImage((prev) => prev !== null ? (prev + 1) % images.length : null);
  const goPrev = () => setSelectedImage((prev) => prev !== null ? (prev - 1 + images.length) % images.length : null);

  React.useEffect(() => {
    if (selectedImage === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "Escape") setSelectedImage(null);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedImage]);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={`${t("gallery.title")} — EkoKompakt`}
        description={t("gallery.subtitle")}
        canonical={`https://ekokompakt.cz${localePath("/galerie")}`}
        ogImage="https://ekokompakt.cz/og-image.png"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ImageGallery",
          "name": t("gallery.title"),
          "description": t("gallery.subtitle"),
          "url": "https://ekokompakt.cz/galerie",
        }}
      />
      <Header />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6">
          <m.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center mb-12">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">{t("gallery.title")}</h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">{t("gallery.subtitle")}</p>
          </m.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {images.map((image, index) => (
              <m.div key={index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: index * 0.05 }} className="group cursor-pointer overflow-hidden rounded-xl border border-border bg-card" onClick={() => setSelectedImage(index)}>
                <div className="aspect-[4/3] overflow-hidden">
                  <OptimizedImage
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    sizes={GALLERY_SIZES}
                    width={400}
                    height={300}
                  />
                </div>
                <div className="p-3">
                  <p className="text-sm text-muted-foreground">{image.alt}</p>
                </div>
              </m.div>
            ))}
          </div>
        </div>
      </main>

      <AnimatePresence>
        {selectedImage !== null && (
          <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}
            onTouchStart={(e) => { (e.currentTarget as any)._touchStartX = e.touches[0].clientX; }}
            onTouchEnd={(e) => { const startX = (e.currentTarget as any)._touchStartX; if (startX == null) return; const diff = startX - e.changedTouches[0].clientX; if (Math.abs(diff) > 50) { e.stopPropagation(); diff > 0 ? goNext() : goPrev(); } }}
          >
            <button onClick={() => setSelectedImage(null)} className="absolute top-4 right-4 text-white/80 hover:text-white z-10"><X className="w-8 h-8" /></button>
            <button onClick={(e) => { e.stopPropagation(); goPrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors z-10"><ChevronLeft className="w-8 h-8" /></button>
            <button onClick={(e) => { e.stopPropagation(); goNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors z-10"><ChevronRight className="w-8 h-8" /></button>
            <m.div key={selectedImage} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="max-w-full max-h-[90vh] rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <OptimizedImage
                src={images[selectedImage].src}
                alt={images[selectedImage].alt}
                className="w-full h-full object-contain"
                width={1200}
                height={900}
              />
            </m.div>
          </m.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default Gallery;
